import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { FieldLabel, SelectInput } from "@td/ui";
import { translate } from "@td/shared_utils";
import { createPracticeTeamParticipant } from "../../apis/createPracticeTeamParticipant";
import { fetchAdmins } from "../../apis/fetchAdmins";
import { ParticipantFormContainer } from "../../components";
import { map, debounce } from "lodash";
import { usePracticeTeam } from "../../contexts/practice-team-context";
import toastr from "toastr";

const LOAD_OPTIONS_LIMIT = 8;
const LOAD_OPTIONS_DEBOUNCE_DELAY = 500;

const CreateParticipantForm = ({ participants, onSuccess, onCancel }) => {
  const [participant, setParticipant] = useState(null);
  const { practiceTeam } = usePracticeTeam();

  const handleChange = (value) => {
    setParticipant(value);
  };

  const handleSubmit = async () => {
    const { value: participantId } = participant;
    const { success } = await createPracticeTeamParticipant(participantId, practiceTeam.id);

    if (success) {
      toastr.success(
        translate(null, "practice_team.participants.creation", "notification")
      );

      onSuccess();
    } else {
      toastr.error(
        translate(null, "practice_team", "server_error")
      );
    }
  };

  const loadOptions = useCallback(async (inputValue, callback) => {
    const participantIds = map(participants, ({ participantId }) => participantId);

    const admins = await fetchAdmins({
      nameQueryStr: inputValue,
      emailQueryStr: inputValue,
      except: participantIds
    }, LOAD_OPTIONS_LIMIT);

    callback(admins.map(({ id, name }) => ({
      value: parseInt(id),
      label: name,
    })));
  }, [participants]);

  const debouncedLoadOptions = useMemo(() => {
    return debounce((inputValue, callback) => {
      loadOptions(inputValue, callback);
    }, LOAD_OPTIONS_DEBOUNCE_DELAY);
  }, [loadOptions]);

  return (
    <ParticipantFormContainer>
      <FieldLabel>
        {translate(null, "practice_team.participants.creation.fields.admin", "label")}:

        <SelectInput
          async
          isClearable
          loadOptions={debouncedLoadOptions}
          value={participant}
          placeholder={translate(null, "practice_team.participants.creation.fields.admin", "placeholder")}
          onChange={handleChange}
        />
      </FieldLabel>

      <div className="buttons">
        <button
          type="button"
          className="secondary button"
          onClick={onCancel}
          data-testid="create-participant-form-cancel-button"
        >
          {translate(null, "practice_team.participants.creation.actions", "cancel")}
        </button>
        <button
          type="button"
          className="button"
          disabled={!participant}
          onClick={handleSubmit}
          data-testid="create-participant-form-save-button"
        >
          {translate(null, "practice_team.participants.creation.actions", "save")}
        </button>
      </div>
    </ParticipantFormContainer>
  );
};

CreateParticipantForm.propTypes = {
  participants: PropTypes.array.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

CreateParticipantForm.displayName = "CreateParticipantForm";

export default CreateParticipantForm;
